import React from 'react'
import { Box, Button, Element, Tag } from 'react-bulma-components'
import { BiRefresh } from 'react-icons/bi'
import { Translate } from '../../utils/translations'

/**
 * Props for the WizardHistory component
 */
interface WizardHistoryProps {
  /** Current step of the wizard */
  currentStep: string
  /** Array of completed steps (dans l'ordre de visite) */
  completedSteps: string[]
  /** Function to navigate to a specific step */
  onStepClick: (step: string) => void
  /** Reset the wizard to its initial step and clear persisted state */
  onReset: () => void
  /** Whether to show the history (hidden on mobile) */
  showHistory: boolean
}

/**
 * Affiche l'historique réel du wizard (étapes visitées + courante)
 * Pas d'étape future, pas de preview, pas de texte pour une étape non visitée
 */
const WizardHistory: React.FC<WizardHistoryProps> = ({
  currentStep,
  completedSteps,
  onStepClick,
  onReset,
  showHistory,
}) => {
  // Historique réel : étapes visitées + courante
  const steps = [...completedSteps, currentStep]

  if (!showHistory) {
    return null
  }

  return (
    <Box mb={4} style={{ position: 'relative' }}>
      <Button
        size="small"
        color="light"
        onClick={onReset}
        aria-label="Reset form"
        title="Reset form"
        data-testid="wizard-reset"
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1,
        }}
      >
        <BiRefresh size={18} />
      </Button>

      <Element
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          paddingRight: '2.5rem',
        }}
      >
        {steps.map((step, index) => {
          const isCompleted = index < steps.length - 1
          const isFinalStep = isFinalStepType(step)
          const isCurrentStepFinal = isFinalStepType(currentStep)

          // Désactiver les liens si on est sur une étape finale
          const canNavigate = isCompleted && !isCurrentStepFinal

          return (
            <React.Fragment key={step}>
              {/* Step indicator */}
              <Element
                data-testid={`history-step-${step}`}
                data-completed={isCompleted}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: canNavigate ? 'pointer' : 'default',
                }}
                onClick={() => canNavigate && onStepClick(step)}
              >
                <Tag
                  color={step === currentStep && !isFinalStep ? 'primary' : 'success'}
                  size="medium"
                  style={{
                    minWidth: '2rem',
                    height: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: step === currentStep ? 'bold' : 'normal',
                  }}
                >
                  {index + 1}
                </Tag>
                <Element
                  ml={2}
                  style={{
                    fontSize: '0.875rem',
                    color: canNavigate ? '#3273dc' : '#7a7a7a',
                    textDecoration: canNavigate ? 'underline' : 'none',
                  }}
                >
                  {isFinalStep ? (
                    <span style={{ color: '#48c774', fontSize: '1.2rem' }}>✓</span>
                  ) : (
                    <Translate value={getStepLabel(step)} />
                  )}
                </Element>
              </Element>
            </React.Fragment>
          )
        })}
      </Element>
      {/* Back button
      {completedSteps.length > 0 && (
        <Element mt={3}>
          <Button
            size="small"
            color="light"
            onClick={() => {
              const previousStep = completedSteps[completedSteps.length - 1]
              if (previousStep) {
                onStepClick(previousStep)
              }
            }}
          >
            <Translate value="wizard.back" />
          </Button>
        </Element>
      )} */}
    </Box>
  )
}

/**
 * Détermine si une étape est une étape finale (affiche une icône de check)
 * @param step - L'étape à vérifier
 * @returns true si l'étape est une étape finale
 */
function isFinalStepType(step: string): boolean {
  // Étapes finales qui affichent une icône de check
  const finalSteps = [
    'repairKitRequestSuccess',
    'unknownNewOwner',
    'notConcerned',
    'ownerChanged.confirmation',
  ]

  return finalSteps.includes(step)
}

/**
 * Retourne la clé de traduction pour chaque étape
 */
function getStepLabel(step: string): string {
  switch (step) {
    case 'emailConfirmation':
      return 'emailConfirmation.title'
    case 'ownerCheck':
      return 'ownerCheck.title'
    case 'ownerChanged':
      return 'ownerChanged.title'
    case 'crankQuantity':
      return 'recall.title'
    case 'concerned':
      return 'repair.title'
    case 'repairKitRequest':
      return 'repairRequest.title'
    case 'repairKitConfirmation':
      return 'repair.infoConfirmation'
    case 'notConcerned':
      return 'recall.notConcerned'
    case 'repairKitRequestSuccess':
      return 'recall.confirmation'
    case 'unknownNewOwner':
      return 'ownerChanged.confirmation'
    case 'userIsOwner':
      return 'userIsOwner.title'
    default:
      return step
  }
}

export default WizardHistory
